import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { SpinnerService } from '@core/services/spinner.service';
import { CyclesAPI } from '@core/typings/api/cycles.typing';
import { FundingSourceTypes } from '@core/typings/budget.typing';
import { ProcessingTypes } from '@core/typings/payment.typing';
import { ProgramApplicantType } from '@core/typings/program.typing';
import { BudgetService } from '@features/budgets/budget.service';
import { CreateEditBudgetModalComponent } from '@features/budgets/create-edit-budget-modal/create-edit-budget-modal.component';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { PaymentProcessingService } from '@features/payment-processing/payment-processing.service';
import { ProgramService } from '@features/programs/services/program.service';
import { UserService } from '@features/users/user.service';
import { SimpleStringMap } from '@yourcause/common';
import { AnalyticsService, EventType } from '@yourcause/common/analytics';
import { SelectOption, TypeSafeFormBuilder, TypeSafeFormGroup, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { DateService, DateTimeRangeValidator, TIMESTAMP_FORMAT } from '@yourcause/common/date';
import { I18nService } from '@yourcause/common/i18n';
import { ModalFactory, YCModalComponent } from '@yourcause/common/modals';
import { ArrayHelpersService } from '@yourcause/common/utils';
import { addYears, endOfDay, getHours, getMinutes, isSameSecond, setHours, setMinutes, setSeconds, startOfDay } from 'date-fns';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { CyclesService } from '../cycles.service';

interface ProgramCycleGroup {
  name: string;
  startDate: Date;
  startTime: Date;
  endDate: Date;
  endTime: Date;
  budgets: number[];
  defaultCashBudgetId: number;
  defaultInKindBudgetId: number;
  defaultCashFundingSourceId: number;
  defaultInKindFundingSourceId: number;
  clientOrganizationsProcessingTypeId: ProcessingTypes;
  alwaysOpen: boolean;
}

@Component({
    selector: 'gc-program-cycle-modal',
    templateUrl: './program-cycle-modal.component.html',
    styleUrls: ['./program-cycle-modal.component.scss'],
    standalone: false
})
export class ProgramCycleModalComponent extends YCModalComponent<
  CyclesAPI.ConfigureProgramCycle
> implements OnInit, OnDestroy {
  @Input() isNew = false;
  @Input() isCopy = false;
  @Input() cycle: CyclesAPI.ConfigureProgramCycle;
  @Input() otherCycles: CyclesAPI.ConfigureProgramCycle[] = [];

  formGroup: TypeSafeFormGroup<ProgramCycleGroup>;
  otherCycleDates: SimpleStringMap<{
    start: string;
    end: string;
  }> = {};
  showProcessorDropdown = false;
  processorOptions = [{
    label: 'YourCause',
    value: ProcessingTypes.YourCause
  }, {
    label: this.clientSettingsService.clientBranding.name,
    value: ProcessingTypes.Client
  }];
  budgetCashOptions: TypeaheadSelectOption[] = [];
  fsCashOptions: TypeaheadSelectOption[] = [];
  budgetInKindOptions: TypeaheadSelectOption[] = [];
  fsInKindOptions: TypeaheadSelectOption[] = [];
  closedText = this.i18n.translate('GLOBAL:textClosed');
  cycleDatesOverlap = false;
  modalHeader: string;
  modalSubHeader: string;
  isIndividual: boolean;
  isBudgetView = false;
  hideFundingSources = this.clientSettingsService.hideFundingSources;
  createBudgetModalOpen = false;
  resetSelectedBudgets = false;
  currentCycleBudgets: number[];
  budgetOptions: SelectOption[] = [];
  sub = new Subscription();
  showDefaultCashBudgetOptions = false;
  showDefaultInKindBudgetOptions = false;
  isAfterInit = false;

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private programService: ProgramService,
    private i18n: I18nService,
    private budgetService: BudgetService,
    private paymentProcessingService: PaymentProcessingService,
    private clientSettingsService: ClientSettingsService,
    private spinnerService: SpinnerService,
    private userService: UserService,
    private cyclesService: CyclesService,
    private arrayHelper: ArrayHelpersService,
    private analyticsService: AnalyticsService,
    private modalFactory: ModalFactory,
    private dateService: DateService
  ) {
    super();

    this.sub.add(this.budgetService.changesTo$('budgets').subscribe(() => {
      if (!!this.budgetService.budgets) {
        this.setBudgetOptions();
      }
    }));
  }

  get isNomination () {
    return location.pathname.includes('nomination');
  }

  get programMap () {
    return this.programService.get('configureProgramMap');
  }

  get activeProgramId () {
    return this.programService.get('activeProgramId');
  }

  get program () {
    return this.programMap[this.activeProgramId];
  }

  get cycleBudgets () {
    return this.formGroup.value.budgets;
  }

  async ngOnInit () {
    this.spinnerService.startSpinner();
    if (!this.cycle) {
      this.cycle = {
        budgets: []
      } as CyclesAPI.ConfigureProgramCycle;
    }
    this.isIndividual = this.program.applicantType === ProgramApplicantType.INDIVIDUAL;
    this.setModalHeader();
    this.setOtherCycleDates();
    this.formGroup = this.formBuilder.group<ProgramCycleGroup>({
      name: [this.cycle.name || '', Validators.required],
      startDate: [
        this.cycle.startDate ?
          new Date(this.cycle.startDate) :
          new Date(),
        Validators.required
      ],
      startTime: [
        this.cycle.startDate ?
          new Date(this.cycle.startDate) :
          startOfDay(new Date())
      ],
      endDate: [
        this.cycle.endDate ?
          new Date(this.cycle.endDate) :
          addYears(new Date(), 1),
        Validators.required
      ],
      endTime: [
        this.cycle.endDate ?
          new Date(this.cycle.endDate) :
          endOfDay(new Date())
      ],
      budgets: this.isNomination ?
        null :
        [this.cycle.budgets || [], Validators.required],
      clientOrganizationsProcessingTypeId: !this.isNomination && !this.isIndividual ?
        [this.cycle.clientOrganizationsProcessingTypeId, Validators.required] :
        null,
      defaultCashBudgetId: this.cycle.defaultCashBudgetId,
      defaultInKindBudgetId: this.cycle.defaultInKindBudgetId,
      defaultCashFundingSourceId: this.cycle.defaultCashFundingSourceId,
      defaultInKindFundingSourceId: this.cycle.defaultInKindFundingSourceId,
      alwaysOpen: this.cycle.alwaysOpen
    }, {
      validators: [
        DateTimeRangeValidator()
      ]
    });
    if (!this.isNomination) {
      this.updateProcessor();
      this.setBudgetOptions();
      await this.budgetService.setFundingSources();
      if (this.cycleBudgets.length > 0) {
        await this.setBudgetDefaultVisibility();
      }
    }
    this.checkCycleOverlap();
    this.isAfterInit = true;
    this.spinnerService.stopSpinner();
  }

  setModalHeader () {
    this.modalSubHeader = this.formGroup?.value.name ?? this.cycle.name ?? '';
    if (this.isBudgetView) {
      this.modalHeader = this.i18n.translate(
        'BUDGET:hdrSelectCycleBudgets',
        {},
        'Select Cycle Budgets'
      );
    } else {
      if (this.isNew) {
        this.modalHeader = this.i18n.translate(
          'PROGRAM:hdrAddNewCycle',
          {},
          'Add New Cycle'
        );
      } else if (this.isCopy) {
        this.modalHeader = this.i18n.translate(
          'PROGRAM:hdrCopyCycle',
          {},
          'Copy Cycle'
        );
      } else {
        this.modalHeader = this.i18n.translate(
          'PROGRAM:hdrEditCycle',
          {},
          'Edit Cycle'
        );
      }
    }
  }

  setBudgetOptions () {
    this.budgetOptions = this.budgetService.budgets.map((budget) => {
      return {
        display: (budget.isClosed ? budget.name + ' (' + this.closedText + ') ' : budget.name),
        value: budget.id
      };
    });
  }

  async onNewBudget () {
    this.createBudgetModalOpen = true;
    const response = await this.modalFactory.open(
      CreateEditBudgetModalComponent,
      {}
    );
    if (!!response) {
      const id = await this.budgetService.handleBudgetModalResponse(response, null, true);
      if (!!id) {
        const updatedBudgetIds = [
          ...this.cycleBudgets,
          +id
        ];
        this.updateBudgetsOnFormGroup(updatedBudgetIds);
      }
    }
    this.createBudgetModalOpen = false;
  }

  isBudgetViewChange (isBudgetView: boolean, isCancelSelection = false) {
    if (!this.isBudgetView && isBudgetView) {
      // If we are switching over to budget selection, make a copy of current budget cycles
      // This is in case they cancel.
      this.setCurrentCycleBudgets();
    } else if (this.isBudgetView && !isBudgetView && isCancelSelection) {
      // If we are coming back from selection but the user canceled,
      // We need to reset the budgets back to what they were
      this.updateBudgetsOnFormGroup(this.currentCycleBudgets);
    }
    this.isBudgetView = isBudgetView;
    this.setModalHeader();
  }

  updateBudgetsOnFormGroup (updatedBudgetIds: number[]) {
    this.formGroup.get('budgets').setValue(updatedBudgetIds);
    // ^ This will trigger budgetsChanged function
  }

  setCurrentCycleBudgets () {
    this.currentCycleBudgets = cloneDeep(this.cycleBudgets);
  }

  onBudgetsChangedFromSelector (budgetIds: number[]) {
    this.updateBudgetsOnFormGroup(budgetIds);
  }

  alwaysOpenChange (alwaysOpen: boolean) {
    const endDateControl = this.formGroup.get('endDate');
    const startDateControl = this.formGroup.get('startDate');
    if (alwaysOpen) {
      startDateControl.setValue(this.dateService.getMinDate());
      endDateControl.setValue(this.dateService.getMaxDate());
    } else {
      startDateControl.setValue(new Date());
      endDateControl.setValue(addYears(new Date(), 1));
    }
  }

  budgetsChanged () {
    this.formGroup.get('clientOrganizationsProcessingTypeId').setValue(null);
    this.updateProcessor();
    this.makeSureDefaultsStillValid();
    this.setBudgetDefaultVisibility();
  }

  selectBudgets () {
    this.isBudgetViewChange(false);
    this.setCurrentCycleBudgets();
  }

  cancelBudgetSelection () {
    this.isBudgetViewChange(false, true);
  }

  updateProcessor () {
    if (
      this.cycleBudgets &&
      this.cycleBudgets.length &&
      !this.isIndividual
    ) {
      const {
        processor,
        hasOptions
      } = this.programService.getProcessorTypeForProgram(
        this.cycle.clientOrganizationsProcessingTypeId,
        this.cycleBudgets,
        this.paymentProcessingService.processorType,
        this.budgetService.budgets
      );
      this.formGroup.get('clientOrganizationsProcessingTypeId').setValue(processor);
      this.showProcessorDropdown = hasOptions;
    } else {
      this.showProcessorDropdown = false;
    }
  }

  makeSureDefaultsStillValid () {
    const {
      cashInvalid,
      inKindInvalid
    } = this.budgetService.areBudgetsStillValid(
      this.cycleBudgets,
      this.formGroup.value.defaultCashBudgetId,
      this.formGroup.value.defaultInKindBudgetId
    );
    if (cashInvalid) {
      this.clearCashBudgetDefaults();
    }
    if (inKindInvalid) {
      this.clearInKindBudgetDefaults();
    }
  }

  clearCashBudgetDefaults () {
    this.formGroup.get('defaultCashBudgetId').setValue(null);
    this.formGroup.get('defaultCashFundingSourceId').setValue(null);
    this.onDefaultCashBudgetChange();
  }

  clearInKindBudgetDefaults () {
    this.formGroup.get('defaultInKindBudgetId').setValue(null);
    this.formGroup.get('defaultInKindFundingSourceId').setValue(null);
    this.onDefaultInKindBudgetChange();
  }

  onDefaultCashBudgetChange (changeFromControl = false) {
    this.setDefaultFsOptions(true, changeFromControl);
  }

  onDefaultInKindBudgetChange (changeFromControl = false) {
    this.setDefaultFsOptions(false, changeFromControl);
  }

  async setBudgetDefaultVisibility () {
    this.spinnerService.startSpinner();
    await this.setDefaultBudgetAndFsOptions(this.cycleBudgets);

    if (this.formGroup) {
      // Attempt defaulting to 1st option
      if (this.budgetCashOptions.length > 0 && !this.formGroup.value.defaultCashBudgetId) {
        this.formGroup.get('defaultCashBudgetId').setValue(this.budgetCashOptions[0].value);
        if (this.fsCashOptions.length > 0) {
          this.formGroup.get('defaultCashFundingSourceId').setValue(this.fsCashOptions[0].value);
        }
      }
      if (this.budgetInKindOptions.length > 0 && !this.formGroup.value.defaultInKindBudgetId) {
        this.formGroup.get('defaultInKindBudgetId').setValue(this.budgetInKindOptions[0].value);
        if (this.fsInKindOptions.length > 0) {
          this.formGroup.get('defaultInKindFundingSourceId').setValue(this.fsInKindOptions[0].value);
        }
      }
    }
    this.showDefaultCashBudgetOptions = this.budgetCashOptions.length > 1 ||
      this.fsCashOptions.length > 1;
    this.showDefaultInKindBudgetOptions = this.budgetInKindOptions.length > 1 ||
      this.fsInKindOptions.length > 1;
    this.spinnerService.stopSpinner();
  }

  async setDefaultBudgetAndFsOptions (budgetIds: number[]) {
    const cashBudgets: number[] = [];
    const inKindBudgets: number[] = [];
    for (const budgetId of budgetIds) {
      await this.budgetService.setBudgetDetail(budgetId);
      const budget = this.budgetService.simpleBudgetMap[budgetId];
      const isCash = budget.fundingSourceType === FundingSourceTypes.DOLLARS;
      if (isCash) {
        cashBudgets.push(budgetId);
      } else {
        inKindBudgets.push(budgetId);
      }
    }

    this.budgetCashOptions = this.getBudgetDefaultOptions(cashBudgets);
    this.budgetInKindOptions = this.getBudgetDefaultOptions(inKindBudgets);
    this.setDefaultFsOptions(true);
    this.setDefaultFsOptions(false);
  }

  setDefaultFsOptions (
    isCash: boolean,
    changeFromControl = false
  ) {
    let fundingSourceIds: number[] = [];
    const defaultBudgetAttr = isCash ? 'defaultCashBudgetId' : 'defaultInKindBudgetId';
    const budgetOptions = isCash ? this.budgetCashOptions : this.budgetInKindOptions;
    let budgetId = this.formGroup.value[defaultBudgetAttr];
    if (!budgetId && budgetOptions.length === 1) {
      budgetId = budgetOptions[0].value;
    }
    if (budgetId) {
      const defaultBudget = this.budgetService.getBudgetDetailFromMap(budgetId);
      if (!!defaultBudget) {
        fundingSourceIds = defaultBudget.budgetFundingSources.map((fs) => {
          return fs.fundingSourceId;
        });
      }
    }
    const optionsAttr = isCash ? 'fsCashOptions' : 'fsInKindOptions';
    this[optionsAttr] = this.getFsDefaultOptions(fundingSourceIds);

    if (changeFromControl) {
      // If the user manually selected a different budget (changeFromControl)
      // We attempt to update the existing funding source ID so it's valid based on new options
      const formGroupAttr = isCash ? 'defaultCashFundingSourceId' : 'defaultInKindFundingSourceId';
      const control = this.formGroup.get(formGroupAttr);
      const fundingSourceId = control.value;
      // If none selected or the one selected is no longer valid
      if (!fundingSourceId || !fundingSourceIds.includes(fundingSourceId)) {
        if (fundingSourceIds.length > 0) {
          const valueToSet = fundingSourceIds[0];
          control.setValue(valueToSet);
        }
      }
    }
  }

  getBudgetDefaultOptions (budgetIds: number[]) {
    return this.arrayHelper.sort(budgetIds.map((budgetId) => {
      const budget = this.budgetService.simpleBudgetMap[budgetId];

      return {
        label: (budget.isClosed ? budget.name + ' (' + this.closedText + ') ' : budget.name),
        value: budgetId
      };
    }), 'label');
  }

  getFsDefaultOptions (fundingSourceIds: number[]) {
    return fundingSourceIds.map((id) => {
      const source = this.budgetService.fundingSourceMap[id];

      return {
        label: (source.isClosed ? source.name + ' (' + this.closedText + ') ' : source.name),
        value: id
      };
    });
  }

  setOtherCycleDates () {
    this.otherCycles.forEach((cycle, index) => {
      this.otherCycleDates[index] = {
        start: this.getDateTime(new Date(cycle.startDate), new Date(cycle.startDate)),
        end: this.getDateTime(new Date(cycle.endDate), new Date(cycle.endDate))
      };
    });
  }

  datesChanged () {
    this.checkCycleOverlap();
  }

  getDate (isStart = true) {
    const timeAttr = isStart ? 'startTime' : 'endTime';
    const dateAttr = isStart ? 'startDate' : 'endDate';
    const dateTime = this.getDateTime(
      this.formGroup.value[timeAttr],
      this.formGroup.value[dateAttr]
    );

    return dateTime;
  }

  getDateTime (timeValue: Date, dateValue: Date) {
    const hours = getHours(timeValue);
    const minutes = getMinutes(timeValue);
    let date = setHours(dateValue, hours);
    date = setMinutes(date, minutes);
    date = setSeconds(date, 0);

    return this.dateService.formatDate(date, TIMESTAMP_FORMAT);
  }

  checkCycleOverlap () {
    let datesOverlap = false;
    const cycleStart = this.getDate(true);
    const cycleEnd = this.getDate(false);
    Object.keys(this.otherCycleDates).forEach((key) => {
      const { start, end } = this.otherCycleDates[key];
      const startIsBetween = this.dateService.isBetween(
        new Date(cycleStart),
        new Date(start),
        new Date(end)
      );
      const endIsBetween = this.dateService.isBetween(
        new Date(cycleEnd),
        new Date(start),
        new Date(end)
      );
      const startIsSame = isSameSecond(new Date(cycleStart), new Date(start)) ||
        isSameSecond(new Date(cycleStart), new Date(end));
      const endIsSame = isSameSecond(new Date(cycleEnd), new Date(start)) ||
        isSameSecond(new Date(cycleEnd), new Date(end));
      const otherStartIsBetween = this.dateService.isBetween(
        new Date(start),
        new Date(cycleStart),
        new Date(cycleEnd)
      );
      const otherEndIsBetween = this.dateService.isBetween(
        new Date(end),
        new Date(cycleStart),
        new Date(cycleEnd)
      );
      if (
        startIsBetween || endIsBetween || startIsSame || endIsSame ||
        otherStartIsBetween || otherEndIsBetween
      ) {
        datesOverlap = true;
      }
    });
    this.cycleDatesOverlap = datesOverlap;
  }

  onSave () {
    const currentUserName = this.userService.user.firstName + ' ' + this.userService.user.lastName;
    if (this.isNew || this.isCopy) {
      this.cycle.createdBy = currentUserName;
      this.cycle.createdDate = new Date().toString();
    }
    this.cycle.updatedBy = currentUserName;
    this.cycle.updatedDate = new Date().toString();
    this.cycle.status = this.cyclesService.getCycleStatus(
      this.cycle.startDate,
      this.cycle.endDate
    );
    const formVal = this.formGroup.value;
    this.cycle = {
      ...this.cycle,
      name: formVal.name,
      budgets: formVal.budgets,
      defaultCashBudgetId: formVal.defaultCashBudgetId,
      defaultInKindBudgetId: formVal.defaultInKindBudgetId,
      defaultCashFundingSourceId: formVal.defaultCashFundingSourceId,
      defaultInKindFundingSourceId: formVal.defaultInKindFundingSourceId,
      clientOrganizationsProcessingTypeId: formVal.clientOrganizationsProcessingTypeId,
      startDate: this.getDate(true),
      endDate: this.getDate(false),
      alwaysOpen: formVal.alwaysOpen
    };
    this.closeModal.emit(this.cycle);
    this.analyticsService.emitEvent({
      eventName: 'Program cycle modal submit',
      eventType: EventType.Click,
      extras: null
    });
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
